import { useState, type FormEvent } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const navItems = [
  { to: '/', key: 'navHome', icon: 'ri-home-5-line' },
  { to: '/category/movie', key: 'navMovie', icon: 'ri-film-line' },
  { to: '/category/tv', key: 'navTv', icon: 'ri-tv-2-line' },
  { to: '/category/variety', key: 'navVariety', icon: 'ri-mic-line' },
  { to: '/favorites', key: 'navFavorites', icon: 'ri-heart-3-line' },
  { to: '/about', key: 'navAbout', icon: 'ri-information-line' },
];

export default function SiteHeader() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  const isActive = (to: string) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname.startsWith(to);
  };

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
    setMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-background-200/70 bg-background-50/90 backdrop-blur-md">
      <div className="flex h-16 items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer" onClick={() => setMenuOpen(false)}>
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-play-fill text-xl"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-extrabold tracking-tight text-foreground-950">久久久精品</span>
            <span className="hidden text-[10px] tracking-widest text-foreground-400 sm:block">影视资源聚合平台</span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={`flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition cursor-pointer ${
                isActive(item.to)
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-950'
              }`}
            >
              <i className={`${item.icon} text-base`}></i>
              {t(item.key)}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden w-56 items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2 lg:flex xl:w-72">
          <i className="ri-search-line text-base text-foreground-400"></i>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder={t('searchPlaceholder')}
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            aria-label={t('navSearch')}
          />
        </form>

        <Link
          to="/search"
          className="ml-auto flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-600 transition hover:bg-background-100 cursor-pointer lg:hidden"
          aria-label={t('navSearch')}
        >
          <i className="ri-search-line text-lg"></i>
        </Link>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-700 transition hover:bg-background-100 cursor-pointer md:hidden"
          aria-label="菜单"
        >
          <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg`}></i>
        </button>
      </div>

      {menuOpen ? (
        <div className="border-t border-background-200 bg-background-50 px-4 py-3 md:hidden">
          <form onSubmit={submitSearch} className="mb-3 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2">
            <i className="ri-search-line text-base text-foreground-400"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder={t('searchPlaceholder')}
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            />
          </form>
          <div className="grid grid-cols-2 gap-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMenuOpen(false)}
                className={`flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium transition cursor-pointer ${
                  isActive(item.to) ? 'bg-primary-100 text-primary-700' : 'bg-background-100 text-foreground-700'
                }`}
              >
                <i className={`${item.icon} text-base`}></i>
                {t(item.key)}
              </NavLink>
            ))}
          </div>
        </div>
      ) : null}
    </header>
  );
}